/**
 * Rail Areas
 *
 * The sidebar renders as a Monday-style icon rail + contextual panel. Each
 * rail area groups the existing nav items; assignment defaults to the item's
 * section with per-route overrides below.
 *
 * IMPORTANT: this file only maps items to areas for presentation. All
 * feature-flag, subscription, permission, account-type and hidden/"More"
 * gating stays in useNavigationItems.tsx + AppSidebar.tsx untouched, so
 * modules that are currently gated off (or intentionally not in the nav)
 * remain gated exactly as before and can be reintroduced later.
 */

import type { ComponentType } from 'react'

import type { SectionKey } from '@scrambl/shared'

import { createLocalIcon } from '../components/local-icon'

/**
 * System-wide navigation style — a build-level setting that applies to every
 * user (this is NOT a per-user preference). 'rail' = icon rail with hover
 * flyouts; 'homeBase' = full sidebar with pinned items + collapsible groups.
 * Override per environment with VITE_NAVIGATION_STYLE; otherwise edit the
 * default here.
 */
export const NAVIGATION_STYLE: 'rail' | 'homeBase' =
  (import.meta.env.VITE_NAVIGATION_STYLE as 'rail' | 'homeBase' | undefined) ??
  'homeBase'

export type RailAreaKey =
  | 'home'
  | 'work'
  | 'sales'
  | 'service'
  | 'operations'
  | 'finance'

export interface RailAreaDef {
  key: RailAreaKey
  label: string
  /** Label override for personal accounts ("People & Home"). */
  personalLabel?: string
  icon: ComponentType<{ className?: string }>
  /** Area accent used for the active rail icon + panel header dot. */
  accent: string
}

/** Rail order top to bottom. Areas with no visible items don't render. */
export const RAIL_AREAS: RailAreaDef[] = [
  {
    key: 'home',
    label: 'Home',
    icon: createLocalIcon('home-outline'),
    accent: '#F2B41C',
  },
  {
    key: 'work',
    label: 'Work',
    icon: createLocalIcon('folder-outline'),
    accent: '#5B8DEF',
  },
  {
    key: 'sales',
    label: 'Sales',
    icon: createLocalIcon('trending-up-outline'),
    accent: '#3FB577',
  },
  {
    key: 'service',
    label: 'Support',
    icon: createLocalIcon('chatbubble-outline'),
    accent: '#8E77F5',
  },
  {
    key: 'operations',
    label: 'Operations',
    personalLabel: 'People & Home',
    icon: createLocalIcon('people-outline'),
    accent: '#E8836B',
  },
  {
    key: 'finance',
    label: 'Finance',
    icon: createLocalIcon('card-outline'),
    accent: '#2FB3A9',
  },
]

/** Section → rail area default assignment. */
export const SECTION_TO_AREA: Record<SectionKey, RailAreaKey> = {
  main: 'home',
  work: 'work',
  sales: 'sales',
  operations: 'operations',
  finance: 'finance',
}

/**
 * Per-route overrides pulling items out of their section's default area, so
 * each area answers one question:
 * - Support gathers the helpdesk surfaces (tickets, chat, KB, intake forms)
 * - Work gets Entities beside Projects — delivery inhabits the client record
 *   (projects, tickets, time, contracts); sales only passes through it
 * - Finance owns quote-to-cash (Quotes stays; the product price book moved
 *   to Operations — Categories/Locations/Movements/Adjustments are stock
 *   operations, and Quotes reads Products as a data link, not a nav one)
 * The nav-item definitions (and all gating) stay in useNavigationItems.
 * (My Organization has no override — it stays in its Work section, which
 * lists it first.)
 */
export const NAV_AREA_OVERRIDES: Record<string, RailAreaKey> = {
  '/tickets': 'service',
  '/live-chat': 'service',
  '/knowledge-base': 'service',
  '/forms': 'service',
  '/entities': 'work',
  '/quotes': 'finance',
  '/products': 'operations',
}

/**
 * Presentation order inside an area when it interleaves items from multiple
 * sections. Unlisted hrefs (and pinned collections) keep their original
 * relative order after the listed ones. Areas not listed already read well.
 */
export const AREA_ITEM_ORDER: Partial<Record<RailAreaKey, string[]>> = {
  work: ['/my-organization', '/projects', '/entities', '/tasks'],
  service: ['/tickets', '/live-chat', '/knowledge-base', '/forms'],
  finance: ['/financials', '/quotes', '/invoices', '/contracts', '/expenses'],
  operations: ['/team', '/suppliers', '/timesheets', '/workflows', '/products'],
}

export function getRailAreaForNavItem(
  href: string,
  section: SectionKey | undefined,
  options?: { isPersonal?: boolean },
): RailAreaKey {
  // Personal accounts keep their section-shaped nav (People & Home already
  // folds Contacts/Suppliers in), so the business re-categorization
  // overrides don't apply there.
  if (!options?.isPersonal) {
    const override = NAV_AREA_OVERRIDES[href]
    if (override) {
      return override
    }
  }
  return section ? SECTION_TO_AREA[section] : 'home'
}
